import React, { useState, useEffect } from 'react';
import { 
  FileText, Loader2, Search, Download, Eye,
  Book, Scale, Gavel, FileCheck, Calendar
} from 'lucide-react';
import { studentPanelAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const categoryIcons = {
  regulation: <Book size={20} />,
  decree: <Gavel size={20} />,
  law: <Scale size={20} />,
  order: <FileCheck size={20} />,
  other: <FileText size={20} />
};

const categoryColors = {
  regulation: '#3b82f6',
  decree: '#10b981',
  law: '#8b5cf6',
  order: '#f59e0b',
  other: '#6b7280'
};

const StudentRegulations = () => {
  const [regulations, setRegulations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    fetchRegulations();
  }, [filterCategory]);

  const fetchRegulations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      
      const response = await studentPanelAPI.getRegulations(params);
      setRegulations(response.data.regulations || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRegulations();
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('uz-UZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Scale size={28} /> Nizom va qarorlar</h1>
          <p>Hukumat qarorlari va ichki nizomlar</p>
        </div>
      </header>

      {/* Kategoriya filterlari */}
      <div className="category-chips" style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <button 
          className={`chip ${filterCategory === '' ? 'active' : ''}`}
          onClick={() => setFilterCategory('')}
          style={{
            padding: '10px 20px',
            borderRadius: '25px',
            border: filterCategory === '' ? 'none' : '1px solid #e2e8f0',
            background: filterCategory === '' ? '#3b82f6' : 'white',
            color: filterCategory === '' ? 'white' : '#64748b',
            cursor: 'pointer',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FileText size={18} />
          Barchasi
        </button>
        {categories.map(cat => (
          <button 
            key={cat.value}
            className={`chip ${filterCategory === cat.value ? 'active' : ''}`}
            onClick={() => setFilterCategory(cat.value)}
            style={{
              padding: '10px 20px',
              borderRadius: '25px',
              border: filterCategory === cat.value ? 'none' : '1px solid #e2e8f0',
              background: filterCategory === cat.value ? categoryColors[cat.value] : 'white',
              color: filterCategory === cat.value ? 'white' : '#64748b',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {categoryIcons[cat.value]}
            {cat.label}
          </button>
        ))}
      </div>

      {/* Qidirish */}
      <form onSubmit={handleSearch} className="search-bar" style={{ marginBottom: '24px', maxWidth: '400px' }}>
        <Search size={20} />
        <input 
          type="text" 
          placeholder="Qidirish..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </form>

      {/* Hujjatlar ro'yxati */}
      {regulations.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Hujjatlar topilmadi</p>
        </div>
      ) : (
        <div className="regulations-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px'
        }}>
          {regulations.map(reg => (
            <div 
              key={reg.id} 
              className="regulation-card"
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${categoryColors[reg.category]}15`,
                  color: categoryColors[reg.category],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {categoryIcons[reg.category]}
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{
                    fontSize: '12px',
                    color: categoryColors[reg.category],
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {reg.category_display}
                  </span>
                  <h3 style={{ margin: '4px 0 0', fontSize: '16px', color: '#1e293b', lineHeight: '1.4' }}>
                    {reg.title}
                  </h3>
                </div>
              </div>
              
              {reg.description && (
                <p style={{ 
                  color: '#64748b', 
                  fontSize: '14px', 
                  margin: '0 0 16px',
                  lineHeight: '1.5'
                }}>
                  {reg.description.substring(0, 150)}{reg.description.length > 150 ? '...' : ''}
                </p>
              )}
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                paddingTop: '16px',
                borderTop: '1px solid #f1f5f9'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#94a3b8', fontSize: '13px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} />
                    {formatDate(reg.created_at)}
                  </span>
                  <span>•</span>
                  <span>{formatFileSize(reg.file_size)}</span>
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a 
                    href={getFileUrl(reg.file_url)} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-icon"
                    style={{ 
                      background: '#f1f5f9', 
                      color: '#475569',
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none'
                    }}
                    title="Ko'rish"
                  >
                    <Eye size={18} />
                  </a>
                  <a 
                    href={getFileUrl(reg.file_url)} 
                    download
                    className="btn btn-icon btn-info"
                    style={{ 
                      background: '#dbeafe', 
                      color: '#2563eb',
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none'
                    }}
                    title="Yuklab olish"
                  >
                    <Download size={18} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentRegulations;
